/**
 * Where to land after the NEXT magic-link sign-in completes.
 *
 * The magic-link flow's destination is fixed: the emailed GET hands the
 * browser to /join/confirm, and the confirming POST answers "/clubhouse".
 * That is right for the join flow and wrong for a tokened invite page, where
 * the member signed in mid-task and should be returned to it. This is the
 * SPA-side patch: the invite page records itself here, and JoinConfirmScreen
 * consults the record before falling back to the server's answer.
 *
 * Deliberately narrow, in three ways:
 *
 *  - ALLOWLISTED. Only paths under /join/quartermaster/ are honored, so a
 *    stray or tampered value can never steer a fresh sign-in anywhere the
 *    invite flow doesn't own. (Navigation is same-origin SPA routing either
 *    way; the prefix keeps intent explicit.)
 *  - SHORT-LIVED. A magic link lives fifteen minutes; the record lives
 *    thirty. An invite page visited this morning must not redirect tonight's
 *    unrelated sign-in.
 *  - SAME-DEVICE ONLY, by nature of sessionStorage. Requesting the link on a
 *    laptop and opening it on a phone lands on /clubhouse as ever, and the
 *    emailed invite link is still in the inbox to finish the errand — a
 *    degraded landing, never a broken one.
 */

const KEY = 'fireside:post-sign-in-return'
const ALLOWED_PREFIX = '/join/quartermaster/'
const TTL_MS = 30 * 60 * 1000

export function rememberSignInReturn(path: string): void {
  if (!path.startsWith(ALLOWED_PREFIX)) return
  try {
    sessionStorage.setItem(KEY, JSON.stringify({ path, at: Date.now() }))
  } catch {
    // Storage unavailable (private mode, quota): the sign-in still works and
    // simply lands on /clubhouse.
  }
}

/** Read-and-clear. Null unless a fresh, allowlisted record exists. */
export function consumeSignInReturn(): string | null {
  try {
    const raw = sessionStorage.getItem(KEY)
    sessionStorage.removeItem(KEY)
    if (!raw) return null
    const { path, at } = JSON.parse(raw) as { path?: unknown; at?: unknown }
    if (typeof path !== 'string' || !path.startsWith(ALLOWED_PREFIX)) return null
    if (typeof at !== 'number' || Date.now() - at > TTL_MS) return null
    return path
  } catch {
    return null
  }
}
